<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';
import ContactListMemberDialog from '@/components/dialogs/ContactListMemberDialog.vue';

import { useContactListMembersStore } from '@/stores/contactListMembers.store';
import type { ContactListMemberDto, ProjectModuleDto, Locales } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';
import { getImageUrl } from '@/helpers/image.helper';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const contactListMembersStore = useContactListMembersStore();
const { orderedContactListMembers } = storeToRefs(contactListMembersStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

// COMPUTED
const columns = computed(() => [
  {
    name: 'profile_image',
    label: t('global.label.image'),
    field: (row: ContactListMemberDto) => row.profile_image,
    align: 'left',
  },
  {
    name: 'name',
    label: t('global.label.name'),
    field: (row: ContactListMemberDto) => getTranslation(row, 'name', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'role',
    label: t('global.label.role'),
    field: (row: ContactListMemberDto) => getTranslation(row, 'role', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'organization',
    label: t('global.label.organization'),
    field: (row: ContactListMemberDto) => getTranslation(row, 'organization', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'email',
    label: t('global.label.email'),
    field: (row: ContactListMemberDto) => row.email,
    align: 'left',
  },
  {
    name: 'phone',
    label: t('global.label.phone'),
    field: (row: ContactListMemberDto) => row.phone,
    align: 'left',
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await contactListMembersStore.fetchContactListMembersByModuleId(props.module.id);
  }
});

// FUNCTIONS
const handleAddRow = () => {
  if (props.module.id) {
    const contactListMember: ContactListMemberDto = contactListMembersStore.createContactListMember(props.module.id);
    $q.dialog({
      component: ContactListMemberDialog,
      componentProps: {
        mode: 'add',
        contactListMember,
        languageEnabled: props.languageEnabled,
      },
    });
  }
};

const handleDeleteRow = (row: ContactListMemberDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('project-module.name') }),
    message: t('global.dialogs.deleteMessage', { type: t('project-module.name') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await contactListMembersStore.deleteContactListMember(row);
  });
};

const handleRowClick = (row: ContactListMemberDto) => {
  $q.dialog({
    component: ContactListMemberDialog,
    componentProps: {
      mode: 'edit',
      contactListMember: row,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleReorder = (source: ContactListMemberDto, target: ContactListMemberDto) => {
  contactListMembersStore.updateContactListMembers(source, target);
};

const getProfileImageUrl = (contactListMember: ContactListMemberDto): string | null =>
  getImageUrl(contactListMember.profile_image);
</script>
<template>
  <div class="contact-list-member-list">
    <reorderable-table
      :rows="orderedContactListMembers"
      :columns="columns"
      :title="t('project-module.contactList.title')"
      itemKey="id"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @reorder="handleReorder"
    >
      <template #body-cell-profile_image="slotProps">
        <q-td :props="slotProps">
          <q-avatar size="36px" color="grey-2" text-color="grey-6">
            <img
              v-if="getProfileImageUrl(slotProps.row)"
              :src="getProfileImageUrl(slotProps.row)!"
              alt="profile"
              style="object-fit: cover"
            />
            <q-icon v-else name="o_person" />
          </q-avatar>
        </q-td>
      </template>
    </reorderable-table>
  </div>
</template>
